import { ReactNode } from "react";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export type TableTotal = {
  label: string;
  value: ReactNode;
};

type TableTotalStatProps = TableTotal & {
  isLoading: boolean;
};

/**
 * One total beside a table. Below md the label sits over the value, since two long labels inline do not fit
 * a phone and wrapping the row doubles the footer's height.
 */
export const TableTotalStat = ({ label, value, isLoading }: TableTotalStatProps) => (
  <div className="flex flex-col md:flex-row items-center gap-0.5 md:gap-2 xl:gap-3 text-theme-text">
    <span className="text-xs md:text-lg xl:text-2xl xl:compact:text-lg whitespace-nowrap md:after:content-[':']">{label}</span>
    <span className="grid place-items-center min-w-8 sm:min-w-12 md:min-w-16 xl:min-w-24 xl:compact:min-w-16 px-2 py-0.5 md:px-4 md:py-1 xl:px-6 xl:py-3 xl:compact:px-4 xl:compact:py-1 rounded bg-theme-warning font-bold">
      <FontAwesomeIcon
        icon={faSpinner}
        spin
        className={`col-start-1 row-start-1 text-base md:text-xl xl:text-3xl xl:compact:text-xl transition-opacity ${isLoading ? 'opacity-100' : 'opacity-0'}`}
      />
      <span className={`col-start-1 row-start-1 text-sm sm:text-lg md:text-2xl xl:text-4xl xl:compact:text-2xl whitespace-nowrap transition-opacity ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
        {value}
      </span>
    </span>
  </div>
);

type TableTotalsFooterProps = {
  totals: TableTotal[];
  isLoading: boolean;
};

export const TableTotalsFooter = ({ totals, isLoading }: TableTotalsFooterProps) => (
  <div className="flex justify-end-safe items-center gap-4 md:gap-6 xl:gap-8 px-2 py-1 md:py-2 xl:py-3 xl:compact:py-1.5 mb-4 compact:mb-1 bg-theme-surface-2 border border-theme-table-border border-t-2 border-t-theme-table-heading-border shadow fade-up">
    {totals.map(total => <TableTotalStat key={total.label} {...total} isLoading={isLoading} />)}
  </div>
);
